import type { Deal } from "../types";
import { westDixieResearch } from "./westDixieResearch";

/**
 * Concept-stage land-development record assembled from the West Dixie
 * feasibility package dated 2026-08-03. Confirmed facts are kept separate from
 * underwriting assumptions so later diligence can replace assumptions without
 * erasing the source record.
 */
export function westDixieDeal(makeBase: (over?: Partial<Deal>) => Deal): Deal {
  return makeBase({
    id: "west-dixie-15458",
    kind: "land-development",
    name: "West Dixie - Taylor Park",
    addr: "15458 W Dixie Hwy, North Miami Beach, FL 33162",
    market: "Miami-Dade (North Miami Beach)",
    stage: "Underwriting",
    censusTract: "12086000206",
    yearBuilt: 1957,
    roofAge: 0,
    flood: "X",
    oz: true,
    zoning: false,
    qct: "unknown",
    dda: "unknown",
    usdaRural: "no",
    u1c: 405,
    u1r: 2200,
    u2c: 270,
    u2r: 2600,
    vacPct: 0.08,
    purchase: 0,
    rehab: 330_000_000,
    soft: 80_000_000,
    hasDeficiencies: true,
    exitCap: 0.0525,
    popGrow: 0,
    renterRising: true,
    unemp: 0.04,
    comps: 2,
    development: {
      folio: "07-2216-000-0220",
      owner: "City of North Miami Beach",
      grossAcres: 21.05,
      lotSqFt: 917_098,
      existingBuildings: 5,
      existingBuildingSf: 9_702,
      currentZoning: "CF - Community Facility",
      targetZoning: "MU/NC - Mixed Use Neighborhood Center",
      censusTract: "12086000206",
      leaseYears: 99,
      annualGroundRent: 1,
      assessedValue: 1_909_986,
      marketValue: 3_511_986,
      asOf: "2026-08-04",
      recommendation:
        "Do not treat Taylor Park as an entitled apartment site. First establish net developable upland acreage and a financeable public-private control structure; then test a phased, transit-oriented mixed-use district through formal FLUM and zoning approvals.",
      facts: [
        "21.05 gross parcel acres (917,098 sf). The City parcel geometry and 2025 county aerial appear to encompass the quarry lake; net upland and net developable acreage are not established.",
        "City-owned and proposed as a $1/year, 99-year ground lease; improvements and leasehold taxation require counsel review.",
        "The parcel is not vacant: five legacy buildings total about 9,702 sf and date from 1957-1980.",
        "Current CF zoning and Recreation/Open Space future land use do not allow the proposed private market-rate mixed-use program by right.",
        "FEMA Zone X, outside the Special Flood Hazard Area; long-term groundwater and sea-level risk still apply.",
        "Active Taylor Park brownfield/cleanup site (ERIC_9217 / BF131501001); current state and county registries show no SRCO closure date.",
        "Opportunity Zone tract 12086000206; confirm post-2026 investor benefits with tax counsel.",
        "Immediate market thesis: younger, attainable-price households spilling from Aventura, reinforced by SoLe Mia and the Northeast transit corridor.",
      ],
      entitlementPath: [
        "Complete survey/title, formal ASTM E1527-21 Phase I and targeted Phase II, traffic, utility-capacity, drainage, and geotechnical studies.",
        "Process the applicable Future Land Use Map amendment and CF-to-an-eligible-mixed-use rezoning with Planning & Zoning and City Commission; amendment procedure and target district require a formal City letter.",
        "Pair legislative approvals with a development agreement and financeable ground-lease protections.",
        "Obtain site-plan approval, likely replat, environmental/stormwater permits, utility will-serve letters, and building permits.",
        "Concept critical path: approximately 24 months to shovel-ready, subject to political, environmental, platting, and concurrency risk.",
      ],
      incentives: [
        "Brownfield Voluntary Cleanup Tax Credit and statutory liability protections, subject to BSRA participation and eligibility.",
        "Qualified Opportunity Zone equity, with the applicable post-2026 rules to be confirmed.",
        "Potential CRA/TIF participation; parcel boundary eligibility and available balance remain unverified.",
        "Potential impact-fee/existing-use credits and public infrastructure participation.",
        "The nominal ground lease removes land acquisition cost but does not remove ad valorem or leasehold tax exposure.",
      ],
      scenarios: [
        {
          id: "base",
          name: "Base - six-story district",
          note: "Package range: roughly 600-750 units and 40-60k sf retail. Uses conservative midpoint assumptions.",
          units: 675,
          oneBrPct: 0.6,
          oneBrRent: 2200,
          twoBrRent: 2600,
          retailSf: 50_000,
          retailRentPsf: 35,
          otherIncomePerUnitMonthly: 125,
          vacancyPct: 0.08,
          expensePct: 0.38,
          exitCap: 0.0525,
          costLow: 330_000_000,
          costHigh: 490_000_000,
          parkingRatio: 1.25,
        },
        {
          id: "max-transit",
          name: "Max transit - eight-story district",
          note: "Package range: roughly 900-1,000+ units and about 75k sf retail; higher entitlement and absorption risk.",
          units: 950,
          oneBrPct: 0.6,
          oneBrRent: 2200,
          twoBrRent: 2600,
          retailSf: 75_000,
          retailRentPsf: 35,
          otherIncomePerUnitMonthly: 125,
          vacancyPct: 0.08,
          expensePct: 0.38,
          exitCap: 0.0525,
          costLow: 470_000_000,
          costHigh: 690_000_000,
          parkingRatio: 1.1,
        },
      ],
      risks: [
        { label: "Parcel identity", status: "verify", detail: "The 21.05-acre GIS parcel appears to include the quarry lake. Survey the water boundary and reconcile gross parcel, upland and net developable acreage before using density or cost-per-acre metrics." },
        { label: "Entitlement", status: "open", detail: "CF zoning and Recreation/Open Space future land use exclude the private mixed-use concept. FLUM and zoning amendments are prerequisites, not approvals already granted." },
        { label: "Environmental", status: "confirmed", detail: "Open brownfield cleanup with cap, groundwater-use restriction, soil-management, and methane/vapor design implications." },
        { label: "Ground lease", status: "verify", detail: "Require mortgagee cure/new-lease rights, transfer rights, environmental indemnity, extension options, and explicit reversion terms." },
        { label: "Sewer capacity", status: "verify", detail: "Confirm downstream Miami-Dade WASD pump-station capacity and moratorium status before fixing density." },
        { label: "Net developable area", status: "verify", detail: "Survey, cap limits, setbacks, drainage, access, open space, and geotechnical work must establish the true buildable footprint." },
        { label: "Market and cost", status: "verify", detail: "Class-A rents, retail rent, operating ratio, cap rate, absorption, construction cost, and escalation need broker/GC validation." },
        { label: "CRA and incentives", status: "verify", detail: "Confirm CRA boundary, available TIF, fee credits, VCTC eligibility, and current Opportunity Zone benefits." },
      ],
      sourceLinks: [
        { label: "Miami-Dade Property Appraiser", url: "https://www.miamidadepa.gov/pa/property_search.asp" },
        { label: "FDEP Taylor Park cleanup file (ERIC_9217)", url: "https://prodenv.dep.state.fl.us/DepNexus/public/electronic-documents/ERIC_9217/facility!search" },
        { label: "Miami-Dade Brownfields program", url: "https://www.miamidade.gov/global/environment/code-compliance/brownfields.page" },
        { label: "NMB Planning & Zoning", url: "https://www.citynmb.com/290/Planning-Zoning" },
        { label: "NMB Mixed-Use Zoning Phase 2", url: "https://www.citynmb.com/1168/Mixed-Use-Form-Based-Zoning---Phase-2" },
        { label: "NMB official zoning GIS", url: "https://gis.citynmb.com/arcgis/rest/services/NorthMiamiBeach/baseEnerGov/MapServer" },
        { label: "NMB CF zoning code - Section 24-55", url: "https://library.municode.com/fl/north_miami_beach/codes/code_of_ordinances?nodeId=PTIICOOR_CHXXIVZOLADE_ARTVZOUSDI" },
        { label: "Miami-Dade 2025 aerial imagery", url: "https://imageserverintra.miamidade.gov/arcgis/rest/services/MapCache/MDCImagery/MapServer" },
      ],
      research: westDixieResearch,
      budget: {
        activeScenarioId: "base",
        residentialGsfPerUnit: 950,
        constructionMonths: 24,
        constructionDebtPct: 0.65,
        constructionRate: 0.08,
        averageDebtUtilization: 0.55,
        affordablePct: 0,
        publicSources: 0,
        permanentLtv: 0.65,
        permanentRate: 0.0625,
        permanentAmortYears: 30,
        rentGrowthPct: 0.03,
        expenseGrowthPct: 0.035,
        saleCostsPct: 0.02,
        overrides: {},
      },
    },
  });
}
